<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            A Google Street View virtual tour puts your business on Google Maps
            — and every search result that surfaces your listing. When a
            potential customer searches for your business category near them,
            your interactive 360° tour appears directly in the search results,
            increasing click-through rates and establishing trust before they
            ever visit.
          </p>
          <p>
            Imagery is placed on <em>your</em> Google Maps location.
            <strong>Travvir is the Street View publisher</strong> shown on that
            place — you do not sign in with a personal Google account in the app.
          </p>
          <p>
            A <strong>single scan</strong> is published by choosing the Maps
            place, selecting scenes, then sending them to Street View. A
            <strong>360° tour</strong> is published by choosing the place and
            sending the linked tour directly. Capture and product details live on
            the
            <a :href="`${mainSiteUrl}/google-streetview`"
              >Google Street View page</a
            >; the in-app steps are in
            <a href="/blog/en/google-street-view"
              >how to publish from Travvir</a
            >.
          </p>
        </section>

        <section>
          <h2>What Is a Google Street View Virtual Tour?</h2>
          <p>
            A Google Street View Business Tour is a 360° virtual tour published
            to Google Maps via your Google Business Profile. It appears in
            Google Search, Maps, and Knowledge Panel — giving searchers an
            immersive look inside your business from the world's largest search
            engine.
          </p>
        </section>

        <section>
          <h2>SEO Benefits of Google Street View Virtual Tours</h2>
          <p>
            Businesses with Google virtual tours see
            <strong>2x higher engagement</strong> on their Google Business
            Profile compared to those without. The tour increases
            time-on-listing, signals to Google that your profile is complete and
            trustworthy, and can positively impact your local pack ranking
            position.
          </p>
        </section>

        <section>
          <h2>How to Create a Google Street View Virtual Tour</h2>
          <h3>
            Option 1 — Capture with Travvir, publish to Google Street View
          </h3>
          <p>
            Create a scan or a linked 360° tour in Travvir, then open
            <strong>Share → Google Street View</strong>. Always start by choosing
            the correct Google Maps place. For a single scan, select the scenes
            and publish. For a tour, publish directly. Travvir is the publisher
            at that place. Google typically reviews within 24–72 hours.
          </p>
          <h3>Option 2 — Use the Street View App Directly</h3>
          <p>
            Download the Google Street View app, tap the camera icon, and follow
            the guided capture process. This is simpler but lacks Travvir's AI
            quality enhancement and multi-room linking. Best for a quick
            single-location tour.
          </p>
          <h3>Step-by-Step Upload Process</h3>
          <p>
            1. Open Google Business Profile Manager. 2. Select "Photos" then
            "360 Photos." 3. Upload your equirectangular images (minimum 7.5
            megapixels). 4. Set the location pin accurately. 5. Submit for
            review — typically approved within 48 hours. 6. Once approved, link
            the tour from your Business Profile's "Virtual Tour" section.
          </p>
        </section>

        <section>
          <h2>Best Practices for Google Business Virtual Tours</h2>
          <p>
            Capture during daylight hours for the best natural light. Include
            your storefront exterior as the entry point. Ensure the space is
            clean and professionally presented. Add multiple interior scenes —
            Google rewards profiles with more content. Update your tour at least
            once a year to keep it current.
          </p>
        </section>
        <section>
          <h2>Continue Reading</h2>
          <ul>
            <li>
              <a :href="`${mainSiteUrl}/google-streetview`"
                >Google Street View with Travvir</a
              >
            </li>
            <li>
              <a href="/blog/en/google-street-view"
                >Publish a scan or tour from Travvir</a
              >
            </li>
            <li>
              <a href="/blog/en/google-business-virtual-tour"
                >Google Business virtual tour</a
              >
            </li>
          </ul>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";
const postData = blogPostsData.find(
  (p) => p.slug === "google-street-view-virtual-tour",
);
const config = useRuntimeConfig();
const mainSiteUrl = config.public.baseUrl;

useBlogSeo({
  title: "Google Street View Virtual Tour: Create & Publish [2026] | Travvir",
  description:
    "Create a Google Street View virtual tour: pick your Maps place, then publish a single scan (select scenes) or a 360° tour directly. Travvir is the publisher at that location.",
  keywords:
    "google street view virtual tour, google maps virtual tour, google business view, 360 tour google maps, business virtual tour google",
  slug: "google-street-view-virtual-tour",
  image: postData.image,
  postData,
});
</script>
